"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  ArrowLeft,
  Calendar,
  Euro,
  Clock,
  CheckCircle2,
  Trash2,
  Plus,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import {
  updateInvoiceStatusAction,
  deleteInvoiceAction,
  createPaymentAction,
  deletePaymentAction,
} from "@/actions/finance-actions";

interface InvoiceDetail {
  id: string;
  amount: number;
  dueDate: string;
  status: "draft" | "sent" | "paid" | "overdue" | "contested";
  projectId: string | null;
  projectTitle: string | null;
  createdAt: string;
}

interface PaymentDetail {
  id: string;
  amount: number;
  paymentDate: string;
  status: "pending" | "cleared";
}

interface InvoiceDetailsClientProps {
  invoice: InvoiceDetail;
  initialPayments: PaymentDetail[];
}

export function InvoiceDetailsClient({
  invoice,
  initialPayments,
}: InvoiceDetailsClientProps) {
  const router = useRouter();
  const [paymentsList, setPaymentsList] =
    useState<PaymentDetail[]>(initialPayments);

  // Payment Modal Form State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<"pending" | "cleared">(
    "cleared",
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Sync payments
  React.useEffect(() => {
    setPaymentsList(initialPayments);
  }, [initialPayments]);

  const totalPaid = paymentsList
    .filter((p) => p.status === "cleared")
    .reduce((sum, p) => sum + p.amount, 0);

  const totalPending = paymentsList
    .filter((p) => p.status === "pending")
    .reduce((sum, p) => sum + p.amount, 0);

  const remainingAmount = Math.max(0, invoice.amount - totalPaid);

  const handleOpenPaymentModal = () => {
    setPaymentAmount(remainingAmount.toString());
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setPaymentStatus("cleared");
    setError("");
    setIsPaymentModalOpen(true);
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const amountNum = parseFloat(paymentAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("L'importo del pagamento deve essere maggiore di 0.");
      return;
    }

    setLoading(true);
    try {
      const res = await createPaymentAction({
        invoiceId: invoice.id,
        amount: amountNum,
        paymentDate: paymentDate || undefined,
        status: paymentStatus,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsPaymentModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante la registrazione del pagamento.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo pagamento?")) return;
    try {
      const res = await deletePaymentAction(paymentId, invoice.id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione del pagamento: " + err.message);
    }
  };

  const handleDeleteInvoice = async () => {
    if (
      !confirm(
        "Sei sicuro di voler eliminare questa fattura e tutti i pagamenti associati? L'azione è irreversibile.",
      )
    )
      return;
    try {
      const res = await deleteInvoiceAction(invoice.id);
      if (res.error) {
        alert(res.error);
      } else {
        router.push("/finance/invoices");
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione della fattura: " + err.message);
    }
  };

  const handleChangeStatus = async (status: any) => {
    try {
      const res = await updateInvoiceStatusAction(invoice.id, status);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'aggiornamento dello stato: " + err.message);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(val);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat("it-IT", { dateStyle: "long" }).format(
      new Date(dateStr),
    );
  };

  const getStatusBadge = (status: InvoiceDetail["status"]) => {
    switch (status) {
      case "draft":
        return <Badge variant="neutral">Bozza</Badge>;
      case "sent":
        return <Badge variant="info">Inviata</Badge>;
      case "paid":
        return <Badge variant="success">Pagata</Badge>;
      case "overdue":
        return <Badge variant="error">Scaduta</Badge>;
      case "contested":
        return <Badge variant="warning">Contestata</Badge>;
    }
  };

  const isOverdue =
    invoice.status !== "paid" && new Date(invoice.dueDate) < new Date();

  return (
    <div className="space-y-6">
      {/* Header with back navigation and quick actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="space-y-1">
          <Link
            href="/finance/invoices"
            className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors mb-2 font-medium"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Torna a Fatture
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold tracking-tight text-text-primary font-mono">
              {invoice.id}
            </h1>
            {getStatusBadge(isOverdue ? "overdue" : invoice.status)}
          </div>
          <p className="text-xs text-text-secondary">
            Creata il {formatDate(invoice.createdAt)}
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <Select
            value={invoice.status}
            onChange={(e) => handleChangeStatus(e.target.value as any)}
            className="w-[140px] text-xs h-9"
          >
            <option value="draft">Bozza</option>
            <option value="sent">Inviata</option>
            <option value="paid">Pagata</option>
            <option value="contested">Contestata</option>
          </Select>

          <Button
            variant="secondary"
            onClick={handleDeleteInvoice}
            className="text-error hover:bg-error-soft hover:text-error hover:border-error/20 gap-1.5 h-9 text-xs"
          >
            <Trash2 className="h-4 w-4" /> Elimina
          </Button>
        </div>
      </div>

      {/* Main Details and Financial Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Financial Info Card */}
        <Card className="md:col-span-2 border-t-4 border-t-category-finance">
          <CardHeader>
            <CardTitle className="text-base font-bold">
              Riepilogo Finanziario
            </CardTitle>
            <CardDescription className="text-xs">
              Valori e scadenze associati a questa fattura
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-4 bg-card-muted/50 p-4 rounded-xl border border-border/40">
              <div>
                <span className="text-[10px] uppercase font-bold text-text-secondary tracking-wider">
                  Importo Fatturato
                </span>
                <p className="text-xl font-extrabold text-text-primary mt-1">
                  {formatCurrency(invoice.amount)}
                </p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-success tracking-wider">
                  Incassato (Cleared)
                </span>
                <p className="text-xl font-extrabold text-success mt-1">
                  {formatCurrency(totalPaid)}
                </p>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-warning tracking-wider">
                  Rimanente
                </span>
                <p className="text-xl font-extrabold text-warning mt-1">
                  {formatCurrency(remainingAmount)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
              <div className="space-y-3">
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-text-secondary">
                    Progetto Associato:
                  </span>
                  <span className="font-medium text-text-primary">
                    {invoice.projectTitle ? (
                      <Link
                        href={`/work/projects/${invoice.projectId}`}
                        className="hover:underline inline-flex items-center gap-0.5 text-primary-600"
                      >
                        {invoice.projectTitle}{" "}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : (
                      <span className="text-text-muted italic">
                        Generico (Nessuno)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-text-secondary">Data Scadenza:</span>
                  <span
                    className={`font-semibold ${isOverdue ? "text-error" : "text-text-primary"}`}
                  >
                    {formatDate(invoice.dueDate)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-text-secondary">
                    Pagamenti In Attesa:
                  </span>
                  <span className="font-semibold text-warning">
                    {formatCurrency(totalPending)}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40">
                  <span className="text-text-secondary">
                    Percentuale Saldata:
                  </span>
                  <span className="font-semibold text-text-primary">
                    {invoice.amount > 0
                      ? `${Math.round((totalPaid / invoice.amount) * 100)}%`
                      : "0%"}
                  </span>
                </div>
              </div>
            </div>

            {/* Overdue alert */}
            {isOverdue && (
              <div className="flex items-start gap-3 bg-error-soft/60 border border-error/20 p-3 rounded-lg text-xs text-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <strong className="font-semibold">
                    Attenzione: Fattura Scaduta!
                  </strong>
                  <p className="mt-0.5">
                    La fattura non risulta interamente saldata ed ha superato la
                    data di scadenza prevista ({formatDate(invoice.dueDate)}).
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress Circle or Status details */}
        <Card className="flex flex-col justify-between">
          <CardHeader>
            <CardTitle className="text-base font-bold">Stato Incassi</CardTitle>
            <CardDescription className="text-xs">
              Percentuale di completamento della transazione
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-6">
            <div className="relative flex items-center justify-center">
              <svg className="w-32 h-32 transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="64"
                  cy="64"
                  r="54"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-border"
                />
                {/* Foreground circle */}
                <circle
                  cx="64"
                  cy="64"
                  r="54"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={2 * Math.PI * 54}
                  strokeDashoffset={
                    2 *
                    Math.PI *
                    54 *
                    (1 -
                      Math.min(100, (totalPaid / invoice.amount) * 100) / 100)
                  }
                  className="text-success transition-all duration-500 ease-out"
                />
              </svg>
              <div className="absolute text-center">
                <span className="text-2xl font-extrabold text-text-primary">
                  {invoice.amount > 0
                    ? Math.round((totalPaid / invoice.amount) * 100)
                    : 0}
                  %
                </span>
                <p className="text-[10px] text-text-muted uppercase font-bold tracking-wider mt-0.5">
                  Saldato
                </p>
              </div>
            </div>
          </CardContent>
          <CardContent className="pt-0 text-center">
            {remainingAmount > 0 ? (
              <Button
                size="sm"
                onClick={handleOpenPaymentModal}
                className="w-full gap-1.5 bg-success hover:bg-success-600 text-white"
              >
                <Plus className="h-4 w-4" /> Registra Pagamento
              </Button>
            ) : (
              <div className="flex items-center justify-center gap-1 text-xs text-success font-semibold py-2">
                <CheckCircle2 className="h-4 w-4" /> Fattura interamente saldata
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payments History Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-bold">
              Registro Pagamenti
            </CardTitle>
            <CardDescription className="text-xs">
              Storico delle transazioni collegate a questa fattura
            </CardDescription>
          </div>
          {remainingAmount > 0 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleOpenPaymentModal}
              className="gap-1.5 h-8 text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Aggiungi Pagamento
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-border/60 bg-card-muted/50 text-text-secondary text-xs uppercase font-semibold">
                <th className="px-6 py-3">ID Pagamento</th>
                <th className="px-6 py-3">Data Pagamento</th>
                <th className="px-6 py-3">Importo</th>
                <th className="px-6 py-3">Stato</th>
                <th className="px-6 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {paymentsList.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-text-muted italic"
                  >
                    Nessun pagamento registrato per questa fattura.
                  </td>
                </tr>
              ) : (
                paymentsList.map((payment) => (
                  <tr
                    key={payment.id}
                    className="hover:bg-card-muted/10 transition-colors"
                  >
                    <td className="px-6 py-3 font-mono text-xs font-semibold text-text-secondary">
                      {payment.id}
                    </td>
                    <td className="px-6 py-3 text-text-primary">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="px-6 py-3 font-bold text-text-primary">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-6 py-3">
                      {payment.status === "cleared" ? (
                        <Badge variant="success">Liquidato</Badge>
                      ) : (
                        <Badge variant="warning">Pendente</Badge>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDeletePayment(payment.id)}
                        className="h-7 w-7 p-0 hover:bg-error-soft hover:text-error hover:border-error/20 inline-flex items-center justify-center"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add Payment Dialog */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Pagamento</DialogTitle>
            <DialogDescription>
              Inserisci i dettagli del pagamento da accreditare su questa
              fattura.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreatePayment} className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pay-amount">Importo (€)</Label>
                <Input
                  id="pay-amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pay-status">Stato Transazione</Label>
                <Select
                  id="pay-status"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="cleared">Saldata / Liquidata (Cleared)</option>
                  <option value="pending">
                    In Sospeso / Pendente (Pending)
                  </option>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Data Pagamento</Label>
              <Input
                id="pay-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsPaymentModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="bg-success hover:bg-success-600 text-white"
              >
                Salva Pagamento
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
